import { NativeSelect, Select, TextInput } from '@common/components';
import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { statusMap, tenantMap } from 'src/constants/statuses';
import { periodMap } from 'src/constants/periods';
import { useTranslation } from 'react-i18next';
import { fetchAdminIssuesSearch, useAppDispatch, useAppSelector } from 'src/store';
import { shallowEqual } from 'react-redux';
import { getDateRange } from 'src/helpers/getDateRange';

import styles from './styles.module.scss';
import { FilterProps } from '@common/types/types';
import { useDisclosure, useMediaQuery, useOs } from '@mantine/hooks';
import { Drawer, Flex, Title, UnstyledButton } from '@mantine/core';
import classNames from 'classnames';
import { Sliders, X } from '@geist-ui/icons';

interface FilterMyReportsProps {
  onIsActive: (item: boolean) => void;
}

const FilterAdminIssues: FC<FilterMyReportsProps> = ({ onIsActive }) => {
  const os = useOs();
  const [status, setStatus] = useState<string | null>('all');
  const [period, setPeriod] = useState<string | null>('all');
  const [owners, setOwners] = useState<string | null>('all');
  const [isActive, setIsActive] = useState<boolean>(false);
  const [t] = useTranslation('reported');
  const [c] = useTranslation('common');
  const dispatch = useAppDispatch();
  const issues = useAppSelector((state) => state.adminIssues, shallowEqual);
  const adminSites = useAppSelector((state) => state.adminSites.data?.siteIds, shallowEqual);
  const [tenantList, setTenantList] = useState<FilterProps[] | []>([]);
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const isMediumDevice = useMediaQuery('(max-width: 992px)');

  useEffect(() => {
    const updatedTenantList = [...tenantMap];
    if (!isActive && adminSites) {
      setTenantList((prev) => {
        Array.from(adminSites).forEach((site) => {
          updatedTenantList.push({
            value: `${site}`,
            label: `${site}`,
          });
        });

        return updatedTenantList;
      });
    }
  }, [adminSites]);

  const handleStatusChange = (value: string | null): void => {
    const { dateFrom, dateTo } = getDateRange(period);
    setStatus(value);
    setIsActive(value !== 'all' ? true : false);
    if (owners) {
      dispatch(
        fetchAdminIssuesSearch({
          params: { siteId: owners, dateTo: dateTo, dateFrom: dateFrom, status: value === 'all' ? '' : value },
        }),
      );
    }
  };

  const handlePeriodChange = (value: string | null): void => {
    const { dateFrom, dateTo } = getDateRange(value);
    setPeriod(value);
    setIsActive(value !== 'all' ? true : false);
    if (owners) {
      dispatch(
        fetchAdminIssuesSearch({
          params: { siteId: owners, dateTo: dateTo, dateFrom: dateFrom, status: status === 'all' ? '' : status },
        }),
      );
    }
  };

  const handleSearch = (value: string): void => {
    dispatch(fetchAdminIssuesSearch({ params: { siteId: owners, search: value } }));
    setIsActive(value !== '' ? true : false);
  };

  const handleOwnersMapChange = (value: string | null): void => {
    const { dateFrom, dateTo } = getDateRange(period);
    setIsActive(value !== 'all' ? true : false);
    setOwners(value);
    dispatch(
      fetchAdminIssuesSearch({
        params: { siteId: value, dateTo: dateTo, dateFrom: dateFrom, status: status === 'all' ? '' : status },
      }),
    );
  };

  const handleNativeStatusChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const { dateFrom, dateTo } = getDateRange(period);
    setStatus(e.currentTarget.value);
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    if (owners) {
      dispatch(
        fetchAdminIssuesSearch({
          params: {
            siteId: owners,
            dateTo: dateTo,
            dateFrom: dateFrom,
            status: e.currentTarget.value === 'all' ? '' : e.currentTarget.value,
          },
        }),
      );
    }
  };

  const handleNativePeriodChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const { dateFrom, dateTo } = getDateRange(e.currentTarget.value);
    setPeriod(e.currentTarget.value);
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    if (owners) {
      dispatch(
        fetchAdminIssuesSearch({
          params: { siteId: owners, dateTo: dateTo, dateFrom: dateFrom, status: status === 'all' ? '' : status },
        }),
      );
    }
  };

  const handleNativeOwnersMapChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const { dateFrom, dateTo } = getDateRange(period);
    setIsActive(e.currentTarget.value !== 'all' ? true : false);
    setOwners(e.currentTarget.value);
    dispatch(
      fetchAdminIssuesSearch({
        params: {
          siteId: e.currentTarget.value,
          dateTo: dateTo,
          dateFrom: dateFrom,
          status: status === 'all' ? '' : status,
        },
      }),
    );
  };

  useEffect(() => {
    onIsActive(isActive);
  }, [isActive]);

  useEffect(() => {
    issues;
  }, [issues]);

  return (
    <div className={styles['filters-list']}>
      {!isMediumDevice ? (
        <Fragment>
          <TextInput
            className={styles.search}
            label={`${t('searchReportInput')}`}
            ariaLabel={`${t('searchReportInput')}`}
            placeholder={t('searchReportInput')}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {os === 'ios' || os === 'macos' ? (
            <>
              <NativeSelect
                variant="secondary"
                aria-label={`${c('combobox')}`}
                value={status as string}
                data={statusMap}
                onChange={handleNativeStatusChange}
              />
              <NativeSelect
                variant="secondary"
                aria-label={`${c('combobox')}`}
                value={period as string}
                data={periodMap}
                onChange={handleNativePeriodChange}
              />
              <NativeSelect
                variant="secondary"
                sx={{
                  '.mantine-Select-dropdown': {
                    width: '350px !important',
                  },
                }}
                value={owners as string}
                data={tenantList}
                onChange={handleNativeOwnersMapChange}
              />
            </>
          ) : (
            <>
              <Select
                variant="secondary"
                aria-label={`${c('combobox')}`}
                withIcon
                value={status}
                data={statusMap}
                onChange={handleStatusChange}
              />
              <Select
                variant="secondary"
                aria-label={`${c('combobox')}`}
                withIcon
                value={period}
                data={periodMap}
                onChange={handlePeriodChange}
              />
              <Select
                variant="secondary"
                sx={{
                  '.mantine-Select-dropdown': {
                    width: '350px !important',
                  },
                }}
                withIcon
                value={owners}
                data={tenantList}
                onChange={handleOwnersMapChange}
              />
            </>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <Flex
            w={'100%'}
            className={classNames(styles['filters-container'])}
            py={'8px'}
            mb={'16px'}
            direction={'column'}>
            <Flex justify={'flex-end'} mb={'10px'}>
              <UnstyledButton
                style={{ transform: 'rotate(270deg)' }}
                ml={'16px'}
                className={classNames(styles['action-btn'])}
                aria-label={`${t('openSettings')}`}
                onClick={() => {
                  toggleDrawer();
                }}>
                <Sliders />
              </UnstyledButton>
            </Flex>

            <TextInput
              className={styles.search}
              label={`${t('searchReportInput')}`}
              ariaLabel={`${t('searchReportInput')}`}
              placeholder={t('searchReportInput')}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Flex>
          <Drawer
            zIndex={1001}
            size={'100%'}
            className={styles.innerWrapperMobile}
            opened={drawerOpened}
            onClose={closeDrawer}
            withCloseButton={false}>
            <Flex mb={'16px'} justify={'space-between'} align={'center'}>
              <Title className={styles['second-heading']} mb={'8px'} order={2}>
                Filters
              </Title>
              <UnstyledButton
                ml={'16px'}
                className={classNames(styles['action-btn'])}
                aria-label={`${t('cancelSettings')}`}
                onClick={() => {
                  toggleDrawer();
                }}>
                <X />
              </UnstyledButton>
            </Flex>

            <Flex gap={'14px'} direction={'column'}>
              {os === 'ios' || os === 'macos' ? (
                <>
                  <NativeSelect
                    variant="secondary"
                    aria-label={`${c('combobox')}`}
                    value={status as string}
                    data={statusMap}
                    onChange={handleStatusChange}
                  />
                  <NativeSelect
                    variant="secondary"
                    aria-label={`${c('combobox')}`}
                    value={period as string}
                    data={periodMap}
                    onChange={handlePeriodChange}
                  />
                  <NativeSelect
                    variant="secondary"
                    sx={{
                      '.mantine-Select-dropdown': {
                        width: '350px !important',
                      },
                    }}
                    value={owners as string}
                    data={tenantList}
                    onChange={handleOwnersMapChange}
                  />
                </>
              ) : (
                <>
                  <Select
                    variant="secondary"
                    aria-label={`${c('combobox')}`}
                    withIcon
                    value={status}
                    data={statusMap}
                    onChange={handleStatusChange}
                  />
                  <Select
                    variant="secondary"
                    aria-label={`${c('combobox')}`}
                    withIcon
                    value={period}
                    data={periodMap}
                    onChange={handlePeriodChange}
                  />
                  <Select
                    variant="secondary"
                    sx={{
                      '.mantine-Select-dropdown': {
                        width: '350px !important',
                      },
                    }}
                    withIcon
                    value={owners}
                    data={tenantList}
                    onChange={handleOwnersMapChange}
                  />
                </>
              )}
            </Flex>
          </Drawer>
        </Fragment>
      )}
    </div>
  );
};

export default FilterAdminIssues;
